// =============================================================================
// Tutoring (Nachhilfe) tracker. Solo, owner-only.
//
// Two screens:
//   • Students list   — all students with the current-semester outstanding
//                       amount and "+ Neuer Schüler".
//   • Student detail  — semester switcher, sessions in that semester,
//                       per-semester totals, "+ Neue Sitzung".
// =============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import {
  GraduationCap,
  Plus,
  ArrowLeft,
  Trash2,
  Pencil,
  Archive,
  ArchiveRestore,
  Clock,
  X,
  ChevronLeft,
  ChevronRight,
  Check,
  Banknote,
  Wallet
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { formatDate as fmt } from '../lib/dates';
import { useToast } from '../lib/Toast';
import { useTranslation } from '../lib/i18n';
import { useConfirm } from '../lib/ConfirmDialog';
import {
  listStudents,
  createStudent,
  updateStudent,
  deleteStudent,
  listSessions,
  addSession,
  updateSession,
  deleteSession,
  listPayments,
  addPayment,
  updatePayment,
  deletePayment,
  semesterFor,
  currentSemester,
  listSemestersFor,
  summarise,
  computeStudentBalance,
  buildTimeline,
  sessionValue
} from '../lib/tutoring';

// -----------------------------------------------------------------------------
// Formatting helpers — local to this component since the formatting needs
// (€, hours) are different from FuelLog (km).
// -----------------------------------------------------------------------------
const moneyFmt = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 2
});
const fmtMoney = (n) => (n == null || !Number.isFinite(n) ? '—' : moneyFmt.format(n));

const fmtHours = (h) => {
  if (h == null || !Number.isFinite(h)) return '—';
  // Display as either "1,5 h" or "45 min" depending on size.
  if (h >= 1) return `${h.toFixed(h % 1 === 0 ? 0 : 2).replace('.', ',')} h`;
  return `${Math.round(h * 60)} min`;
};

const fmtDuration = (mins) => {
  if (mins == null) return '—';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
};

const fmtDate = (s) => {
  try {
    return fmt(parseISO(s), 'medium');
  } catch {
    return s;
  }
};

// =============================================================================
// Top-level entry — switches between list and detail.
// =============================================================================
const Tutoring = () => {
  const [activeStudentId, setActiveStudentId] = useState(null);

  return activeStudentId ? (
    <StudentDetail
      studentId={activeStudentId}
      onBack={() => setActiveStudentId(null)}
    />
  ) : (
    <StudentsList onOpen={setActiveStudentId} />
  );
};

export default Tutoring;

// =============================================================================
// StudentsList — landing page.
// =============================================================================
const StudentsList = ({ onOpen }) => {
  const { t, lang } = useTranslation();
  const toast = useToast();
  const [students, setStudents] = useState(null); // null = loading
  const [allSessions, setAllSessions] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const reload = async () => {
    try {
      const [st, ss, pp] = await Promise.all([
        listStudents({ includeArchived: true }),
        listSessions(),
        listPayments()
      ]);
      setStudents(st);
      setAllSessions(ss);
      setAllPayments(pp);
    } catch (err) {
      toast.error(err.message || t('common.loadFailed'));
      setStudents([]);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cur = useMemo(() => currentSemester(), [lang]);

  // Per-student all-time balance (= sum payments - sum unpaid sessions).
  // Plus a current-semester hours snapshot for the row.
  const perStudent = useMemo(() => {
    const m = new Map();
    if (!students) return m;
    for (const stu of students) {
      const ss = allSessions.filter((s) => s.studentId === stu.id);
      const pp = allPayments.filter((p) => p.studentId === stu.id);
      const balance = computeStudentBalance(ss, pp);
      const semSummary = summarise(ss, cur);
      m.set(stu.id, { balance, semSummary });
    }
    return m;
  }, [students, allSessions, allPayments, cur]);

  // Top-of-page total = NET balance owed to me across all active students
  // (sum of negative balances; credits don't count as "they owe").
  const totalOwed = useMemo(() => {
    if (!students) return 0;
    return students
      .filter((s) => !s.archived)
      .reduce((acc, s) => {
        const b = perStudent.get(s.id)?.balance.balance ?? 0;
        return acc + (b < 0 ? -b : 0);
      }, 0);
  }, [students, perStudent]);
  const totalCredit = useMemo(() => {
    if (!students) return 0;
    return students
      .filter((s) => !s.archived)
      .reduce((acc, s) => {
        const b = perStudent.get(s.id)?.balance.balance ?? 0;
        return acc + (b > 0 ? b : 0);
      }, 0);
  }, [students, perStudent]);

  const visible = (students || []).filter((s) => showArchived || !s.archived);
  const hasArchived = (students || []).some((s) => s.archived);

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 pb-24 sm:pb-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-300">
          <GraduationCap size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {t('nav.tutoring')}
          </h1>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {cur.label}
          </div>
        </div>
      </div>

      {/* Balance summary card */}
      {students && students.length > 0 && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 mb-4 bg-gradient-to-br from-primary-50 to-white dark:from-primary-900/20 dark:to-gray-900">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400">
                {t('tutoring.parentsOweMe')}
              </div>
              <div
                className={`mt-1 text-2xl font-bold ${
                  totalOwed > 0.005
                    ? 'text-amber-700 dark:text-amber-300'
                    : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                {fmtMoney(totalOwed)}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400">
                {t('tutoring.creditAvailable')}
              </div>
              <div
                className={`mt-1 text-2xl font-bold ${
                  totalCredit > 0.005
                    ? 'text-emerald-700 dark:text-emerald-300'
                    : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                {fmtMoney(totalCredit)}
              </div>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => setShowAdd(true)}
        className="w-full mb-4 btn bg-primary-600 text-white hover:bg-primary-700 flex items-center justify-center gap-2"
      >
        <Plus size={16} />
        {t('tutoring.newStudent')}
      </button>

      {students === null ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-sm text-gray-500 dark:text-gray-400">
          {t('common.loading')}
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 p-6 text-center">
          <GraduationCap size={32} className="mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('tutoring.emptyStudents')}
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {visible.map((s) => {
            const stuData = perStudent.get(s.id);
            const bal = stuData?.balance.balance ?? 0;
            const sem = stuData?.semSummary;
            return (
              <li key={s.id}>
                <button
                  onClick={() => onOpen(s.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border bg-white dark:bg-gray-900 hover:border-primary-300 dark:hover:border-primary-700 transition-colors text-left ${
                    s.archived
                      ? 'border-gray-200 dark:border-gray-800 opacity-60'
                      : 'border-gray-200 dark:border-gray-800'
                  }`}
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0"
                    style={{ background: s.color || '#6366f1' }}
                  >
                    {s.name.trim().charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 dark:text-gray-100 truncate flex items-center gap-2">
                      {s.name}
                      {s.archived && (
                        <span className="text-xs font-normal text-gray-400">
                          {t('tutoring.archived')}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {sem ? `${fmtHours(sem.hours)} · ${cur.label}` : '–'}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {Math.abs(bal) < 0.005 ? (
                      <div className="text-xs text-gray-400">
                        {t('tutoring.settled')}
                      </div>
                    ) : bal > 0 ? (
                      <div className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                        +{fmtMoney(bal)}
                        <div className="text-[10px] font-normal text-gray-500 dark:text-gray-400">
                          {t('tutoring.credit')}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                        {fmtMoney(bal)}
                        <div className="text-[10px] font-normal text-gray-500 dark:text-gray-400">
                          {t('widget.open')}
                        </div>
                      </div>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {hasArchived && (
        <button
          onClick={() => setShowArchived((v) => !v)}
          className="mt-4 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
        >
          {showArchived ? t('tutoring.hideArchived') : t('tutoring.showArchived')}
        </button>
      )}

      {showAdd && (
        <StudentFormModal
          onClose={() => setShowAdd(false)}
          onSaved={async (id) => {
            setShowAdd(false);
            await reload();
            toast.success(t('tutoring.studentCreated'));
            if (id) onOpen(id);
          }}
        />
      )}
    </div>
  );
};

// =============================================================================
// StudentDetail — sessions per semester for one student.
// =============================================================================
const StudentDetail = ({ studentId, onBack }) => {
  const { t, lang } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();

  const [student, setStudent] = useState(null);
  const [sessions, setSessions] = useState(null); // null = loading
  const [payments, setPayments] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editProfile, setEditProfile] = useState(false);
  const [activeSemId, setActiveSemId] = useState(null);

  const reload = async () => {
    try {
      const [students, ss, pp] = await Promise.all([
        listStudents({ includeArchived: true }),
        listSessions({ studentId }),
        listPayments({ studentId })
      ]);
      const stu = students.find((s) => s.id === studentId);
      if (!stu) {
        toast.error(t('tutoring.studentNotFound'));
        onBack();
        return;
      }
      setStudent(stu);
      setSessions(ss);
      setPayments(pp);
    } catch (err) {
      toast.error(err.message || t('common.loadFailed'));
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  // Semesters are derived from BOTH sessions and payments so a semester
  // with only a prepayment (no sessions yet) still appears in the list.
  const semesters = useMemo(() => {
    const fakeForPayments = (payments || []).map((p) => ({
      sessionDate: p.paymentDate
    }));
    return listSemestersFor([...(sessions || []), ...fakeForPayments]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, payments, lang]);

  // Default to the current semester (most-recent in the descending list).
  useEffect(() => {
    if (activeSemId == null && semesters.length > 0) {
      const cur = currentSemester();
      const match = semesters.find((s) => s.id === cur.id) || semesters[0];
      setActiveSemId(match.id);
    }
  }, [semesters, activeSemId]);

  const activeSemester = semesters.find((s) => s.id === activeSemId) || currentSemester();
  const summary = useMemo(
    () => summarise(sessions || [], activeSemester),
    [sessions, activeSemester]
  );

  // All-time running balance for this student (across every semester).
  const balance = useMemo(
    () => computeStudentBalance(sessions || [], payments || []),
    [sessions, payments]
  );

  // Unified timeline (sessions + payments) for the active semester, with
  // a per-row running balance.
  const timeline = useMemo(
    () => buildTimeline(sessions || [], payments || [], { semester: activeSemester }),
    [sessions, payments, activeSemester]
  );

  const handleTogglePaid = async (s) => {
    try {
      await updateSession(s.id, { paid: !s.paid });
      await reload();
    } catch (err) {
      toast.error(err.message || t('common.couldNotSave'));
    }
  };

  const handleDelete = async (s) => {
    const ok = await confirm({
      title: t('tutoring.deleteSessionTitle'),
      message: t('tutoring.deleteSessionMessage', { date: fmtDate(s.sessionDate), duration: fmtDuration(s.durationMinutes) }),
      confirmLabel: t('common.delete'),
      destructive: true
    });
    if (!ok) return;
    try {
      await deleteSession(s.id);
      await reload();
    } catch (err) {
      toast.error(err.message || t('common.deleteFailed'));
    }
  };

  const handleDeletePayment = async (p) => {
    const ok = await confirm({
      title: t('tutoring.deletePaymentTitle'),
      message: t('tutoring.deletePaymentMessage', { amount: fmtMoney(p.amount), date: fmtDate(p.paymentDate) }),
      confirmLabel: t('common.delete'),
      destructive: true
    });
    if (!ok) return;
    try {
      await deletePayment(p.id);
      await reload();
    } catch (err) {
      toast.error(err.message || t('common.deleteFailed'));
    }
  };

  const handleArchive = async () => {
    try {
      await updateStudent(studentId, { archived: !student.archived });
      await reload();
      toast.success(student.archived ? t('tutoring.activeAgain') : t('tutoring.archivedToast'));
    } catch (err) {
      toast.error(err.message || t('tutoring.changeFailed'));
    }
  };

  const handleDeleteStudent = async () => {
    const ok = await confirm({
      title: t('tutoring.deleteStudentTitle'),
      message: t('tutoring.deleteStudentMessage', { name: student.name }),
      confirmLabel: t('common.delete'),
      destructive: true
    });
    if (!ok) return;
    try {
      await deleteStudent(studentId);
      toast.success(t('common.deleted'));
      onBack();
    } catch (err) {
      toast.error(err.message || t('common.deleteFailed'));
    }
  };

  // Navigate semesters with arrows.
  const semIdx = semesters.findIndex((s) => s.id === activeSemester.id);
  const goPrev = () => {
    if (semIdx + 1 < semesters.length) setActiveSemId(semesters[semIdx + 1].id);
  };
  const goNext = () => {
    if (semIdx - 1 >= 0) setActiveSemId(semesters[semIdx - 1].id);
  };

  if (!student) {
    return (
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <button
          onClick={onBack}
          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 mb-4"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="text-sm text-gray-500 dark:text-gray-400">Lade…</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 pb-24 sm:pb-6">
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={onBack}
          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label={t('common.back')}
        >
          <ArrowLeft size={18} />
        </button>
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0"
          style={{ background: student.color || '#6366f1' }}
        >
          {student.name.trim().charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate">
            {student.name}
          </h1>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {fmtMoney(student.defaultRate)}/h
            {student.archived && ` · ${t('tutoring.archived')}`}
          </div>
        </div>
        <button
          onClick={() => setEditProfile(true)}
          className="btn-secondary !p-2"
          aria-label={t('common.edit')}
        >
          <Pencil size={16} />
        </button>
      </div>

      {/* Semester switcher */}
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={goPrev}
          disabled={semIdx + 1 >= semesters.length}
          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30"
          aria-label={t('tutoring.prevSemester')}
        >
          <ChevronLeft size={18} />
        </button>
        <select
          value={activeSemester.id}
          onChange={(e) => setActiveSemId(e.target.value)}
          className="input flex-1 !py-1.5 text-sm font-medium"
        >
          {semesters.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
        <button
          onClick={goNext}
          disabled={semIdx <= 0}
          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30"
          aria-label={t('tutoring.nextSemester')}
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Running balance card (all-time) */}
      <BalanceCard balance={balance} />

      {/* Semester stats — small, secondary */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-3 mb-4 bg-white dark:bg-gray-900">
        <div className="grid grid-cols-3 gap-3">
          <SummaryStat label={t('tutoring.hours')} value={fmtHours(summary.hours)} small />
          <SummaryStat label={t('tutoring.earned')} value={fmtMoney(summary.earned)} small />
          <SummaryStat
            label={t('tutoring.paidDirectly')}
            value={fmtMoney(summary.paid)}
            small
          />
        </div>
        <div className="mt-1 text-[10px] text-gray-400 dark:text-gray-500 text-center">
          {activeSemester.label}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <button
          onClick={() => setShowAdd(true)}
          className="btn bg-primary-600 text-white hover:bg-primary-700 flex items-center justify-center gap-2"
        >
          <Plus size={16} />
          {t('tutoring.session')}
        </button>
        <button
          onClick={() => setShowPay(true)}
          className="btn bg-emerald-600 text-white hover:bg-emerald-700 flex items-center justify-center gap-2"
        >
          <Banknote size={16} />
          {t('tutoring.payment')}
        </button>
      </div>

      {/* Unified timeline — sessions + payments, newest first */}
      {sessions === null ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-sm text-gray-500 dark:text-gray-400">
          {t('common.loading')}
        </div>
      ) : timeline.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 p-6 text-center">
          <Clock size={28} className="mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('tutoring.emptySemester', { semester: activeSemester.label })}
          </p>
        </div>
      ) : (
        <ul className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800 overflow-hidden">
          {[...timeline].reverse().map((e) =>
            e.kind === 'session' ? (
              <SessionRow
                key={`s-${e.session.id}`}
                session={e.session}
                onTogglePaid={() => handleTogglePaid(e.session)}
                onEdit={() => setEditing(e.session)}
                onDelete={() => handleDelete(e.session)}
              />
            ) : (
              <PaymentRow
                key={`p-${e.payment.id}`}
                payment={e.payment}
                onEdit={() => setEditingPayment(e.payment)}
                onDelete={() => handleDeletePayment(e.payment)}
              />
            )
          )}
        </ul>
      )}

      {/* Footer actions */}
      <div className="mt-6 flex flex-wrap gap-2">
        <button
          onClick={handleArchive}
          className="btn-secondary text-sm flex items-center gap-1.5"
        >
          {student.archived ? (
            <>
              <ArchiveRestore size={14} />
              {t('tutoring.activeAgain')}
            </>
          ) : (
            <>
              <Archive size={14} />
              {t('tutoring.archive')}
            </>
          )}
        </button>
        <button
          onClick={handleDeleteStudent}
          className="text-sm text-red-600 dark:text-red-400 hover:underline px-3"
        >
          {t('tutoring.deleteStudent')}
        </button>
      </div>

      {/* Modals */}
      {showAdd && (
        <SessionFormModal
          student={student}
          onClose={() => setShowAdd(false)}
          onSaved={async () => {
            setShowAdd(false);
            await reload();
            toast.success(t('tutoring.sessionSaved'));
          }}
        />
      )}
      {showPay && (
        <PaymentFormModal
          student={student}
          // Pre-fill the amount with whatever the parent currently owes,
          // if anything — most common case is "they handed me cash for
          // exactly the outstanding amount".
          suggestedAmount={balance.balance < 0 ? -balance.balance : null}
          onClose={() => setShowPay(false)}
          onSaved={async () => {
            setShowPay(false);
            await reload();
            toast.success(t('tutoring.paymentRecorded'));
          }}
        />
      )}
      {editing && (
        <SessionFormModal
          student={student}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
            toast.success(t('common.updated'));
          }}
        />
      )}
      {editingPayment && (
        <PaymentFormModal
          student={student}
          existing={editingPayment}
          onClose={() => setEditingPayment(null)}
          onSaved={async () => {
            setEditingPayment(null);
            await reload();
            toast.success(t('common.updated'));
          }}
        />
      )}
      {editProfile && (
        <StudentFormModal
          existing={student}
          onClose={() => setEditProfile(false)}
          onSaved={async () => {
            setEditProfile(false);
            await reload();
            toast.success(t('common.updated'));
          }}
        />
      )}
    </div>
  );
};

const SummaryStat = ({ label, value, highlight, small }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400">
      {label}
    </div>
    <div
      className={`${small ? 'mt-0.5 text-sm' : 'mt-1 text-base'} font-bold tabular-nums ${
        highlight
          ? 'text-amber-700 dark:text-amber-300'
          : 'text-gray-900 dark:text-gray-100'
      }`}
    >
      {value}
    </div>
  </div>
);

// =============================================================================
// BalanceCard — the big "Lukas hat 50€ Guthaben" / "Lukas schuldet 25€" line.
// =============================================================================
const BalanceCard = ({ balance }) => {
  const { t } = useTranslation();
  const b = balance.balance;
  const isFlat = Math.abs(b) < 0.005;
  const isCredit = b > 0;
  return (
    <div
      className={`rounded-xl border p-4 mb-3 ${
        isCredit
          ? 'border-emerald-200 dark:border-emerald-900/50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-gray-900'
          : !isFlat
          ? 'border-amber-200 dark:border-amber-900/50 bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/20 dark:to-gray-900'
          : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900'
      }`}
    >
      <div className="text-xs uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1.5">
        <Wallet size={12} />
        {t('savings.balance')}
      </div>
      {isFlat ? (
        <div className="text-base font-medium text-gray-700 dark:text-gray-200">
          {t('tutoring.balanced')}
        </div>
      ) : isCredit ? (
        <div>
          <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
            +{fmtMoney(b)} {t('tutoring.credit')}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t('tutoring.creditHint')}
          </div>
        </div>
      ) : (
        <div>
          <div className="text-2xl font-bold text-amber-700 dark:text-amber-300">
            {fmtMoney(b)} {t('widget.open')}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t('tutoring.owedHint')}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// Timeline rows — sessions and payments share the same list.
// =============================================================================
const SessionRow = ({ session: s, onTogglePaid, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const value = sessionValue(s);
  return (
    <li className="flex items-center gap-2 p-3">
      <button
        onClick={onTogglePaid}
        title={s.paid ? t('tutoring.undoDirectPayment') : t('tutoring.paidDirectlyHint')}
        aria-label={t('tutoring.togglePaid')}
        className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
          s.paid
            ? 'bg-primary-600 border-primary-600 text-white'
            : 'border-gray-300 dark:border-gray-600 hover:border-primary-500'
        }`}
      >
        {s.paid && <Check size={14} strokeWidth={3} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {fmtDate(s.sessionDate)}
          </div>
          <div
            className={`text-sm font-semibold tabular-nums ${
              s.paid
                ? 'text-gray-400 line-through'
                : 'text-amber-700 dark:text-amber-300'
            }`}
          >
            −{fmtMoney(value)}
          </div>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-2 flex-wrap">
          <span>{fmtDuration(s.durationMinutes)}</span>
          <span>·</span>
          <span>{fmtMoney(s.rate)}/h</span>
          {s.note && <span className="truncate">· {s.note}</span>}
        </div>
      </div>
      <button
        onClick={onEdit}
        className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 flex-shrink-0"
        aria-label={t('common.edit')}
      >
        <Pencil size={14} />
      </button>
      <button
        onClick={onDelete}
        className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 flex-shrink-0"
        aria-label={t('common.delete')}
      >
        <Trash2 size={14} />
      </button>
    </li>
  );
};

const PaymentRow = ({ payment: p, onEdit, onDelete }) => {
  const { t } = useTranslation();
  return (
  <li className="flex items-center gap-2 p-3 bg-emerald-50/40 dark:bg-emerald-900/10">
    <div className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 flex items-center justify-center flex-shrink-0">
      <Banknote size={14} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {fmtDate(p.paymentDate)}
        </div>
        <div className="text-sm font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">
          +{fmtMoney(p.amount)}
        </div>
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
        {t('tutoring.paymentReceived')}{p.note ? ` · ${p.note}` : ''}
      </div>
    </div>
    <button
      onClick={onEdit}
      className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 flex-shrink-0"
      aria-label={t('common.edit')}
    >
      <Pencil size={14} />
    </button>
    <button
      onClick={onDelete}
      className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 flex-shrink-0"
      aria-label={t('common.delete')}
    >
      <Trash2 size={14} />
    </button>
  </li>
  );
};

// =============================================================================
// StudentFormModal — create / edit a student.
// =============================================================================
const PRESET_COLORS = [
  '#6366f1', '#0ea5e9', '#10b981', '#f59e0b',
  '#ef4444', '#ec4899', '#8b5cf6', '#14b8a6'
];

const StudentFormModal = ({ existing, onClose, onSaved }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [name, setName] = useState(existing?.name || '');
  const [rate, setRate] = useState(
    existing ? String(existing.defaultRate) : ''
  );
  const [color, setColor] = useState(existing?.color || PRESET_COLORS[0]);
  const [notes, setNotes] = useState(existing?.notes || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (saving) return;
    if (!name.trim()) {
      toast.error(t('tutoring.errNameRequired'));
      return;
    }
    setSaving(true);
    try {
      let id;
      if (existing) {
        await updateStudent(existing.id, {
          name,
          defaultRate: rate || 0,
          color,
          notes
        });
        id = existing.id;
      } else {
        const created = await createStudent({
          name,
          defaultRate: rate || 0,
          color,
          notes
        });
        id = created.id;
      }
      onSaved(id);
    } catch (err) {
      toast.error(err.message || t('common.saveFailed'));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-md p-5 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {existing ? t('tutoring.editStudent') : t('tutoring.newStudent')}
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('common.name')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('tutoring.namePlaceholder')}
              className="input w-full"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('tutoring.hourlyRate')}
            </label>
            <input
              type="number"
              inputMode="decimal"
              step="0.50"
              min="0"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder={t('tutoring.ratePlaceholder')}
              className="input w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('common.color')}
            </label>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    color === c
                      ? 'border-gray-900 dark:border-gray-100 scale-110'
                      : 'border-transparent'
                  }`}
                  style={{ background: c }}
                  aria-label={t('tutoring.colorAria', { color: c })}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('common.notesOptional')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder={t('tutoring.notesPlaceholder')}
              className="input w-full"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={handleSave}
              disabled={saving || !name.trim()}
              className="btn bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex-1"
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
            <button onClick={onClose} className="btn-secondary">
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// SessionFormModal — create / edit a single tutoring session.
// =============================================================================
const SessionFormModal = ({ student, existing, onClose, onSaved }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const today = format(new Date(), 'yyyy-MM-dd');
  const [date, setDate] = useState(existing?.sessionDate || today);
  const [duration, setDuration] = useState(
    existing ? String(existing.durationMinutes) : '60'
  );
  const [rate, setRate] = useState(
    existing ? String(existing.rate) : String(student.defaultRate || '')
  );
  const [note, setNote] = useState(existing?.note || '');
  const [saving, setSaving] = useState(false);

  const dur = Number(duration);
  const r = Number(rate);
  const liveValue =
    Number.isFinite(dur) && Number.isFinite(r) && dur > 0 ? (dur / 60) * r : null;

  const handleSave = async () => {
    if (saving) return;
    if (!Number.isFinite(dur) || dur <= 0) {
      toast.error(t('tutoring.errDurationInvalid'));
      return;
    }
    setSaving(true);
    try {
      if (existing) {
        await updateSession(existing.id, {
          sessionDate: date,
          durationMinutes: dur,
          rate: r,
          note
        });
      } else {
        await addSession({
          studentId: student.id,
          sessionDate: date,
          durationMinutes: dur,
          rate: r,
          note
        });
      }
      onSaved();
    } catch (err) {
      toast.error(err.message || t('common.saveFailed'));
      setSaving(false);
    }
  };

  // Quick duration presets (commonly 45 / 60 / 90 min for tutoring).
  const presets = [45, 60, 75, 90, 120];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-md p-5 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {existing ? t('tutoring.editSession') : t('tutoring.newSession')}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {student.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('common.date')}
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input w-full"
            />
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {semesterFor(date).label}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('common.durationMinutes')}
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {presets.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setDuration(String(m))}
                  className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                    Number(duration) === m
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  {fmtDuration(m)}
                </button>
              ))}
            </div>
            <input
              type="number"
              inputMode="numeric"
              min="1"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="input w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('tutoring.hourlyRate')}
            </label>
            <input
              type="number"
              inputMode="decimal"
              step="0.50"
              min="0"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              className="input w-full"
            />
            {liveValue != null && liveValue > 0 && (
              <div className="mt-1 text-sm text-primary-700 dark:text-primary-300 font-medium">
                = {fmtMoney(liveValue)}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('todo.noteOptional')}
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('tutoring.sessionNotePlaceholder')}
              className="input w-full"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={handleSave}
              disabled={saving || !duration || !date}
              className="btn bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex-1"
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
            <button onClick={onClose} className="btn-secondary">
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// PaymentFormModal — record a lump-sum payment from a student's parents.
// =============================================================================
const PaymentFormModal = ({ student, existing, suggestedAmount, onClose, onSaved }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const today = format(new Date(), 'yyyy-MM-dd');
  const [date, setDate] = useState(existing?.paymentDate || today);
  const [amount, setAmount] = useState(
    existing
      ? String(existing.amount)
      : suggestedAmount
      ? suggestedAmount.toFixed(2)
      : ''
  );
  const [note, setNote] = useState(existing?.note || '');
  const [saving, setSaving] = useState(false);

  const amt = Number(amount);
  const valid = amount !== '' && Number.isFinite(amt) && amt > 0;

  const handleSave = async () => {
    if (saving || !valid) return;
    setSaving(true);
    try {
      if (existing) {
        await updatePayment(existing.id, {
          amount: amt,
          paymentDate: date,
          note
        });
      } else {
        await addPayment({
          studentId: student.id,
          amount: amt,
          paymentDate: date,
          note
        });
      }
      onSaved();
    } catch (err) {
      toast.error(err.message || t('common.saveFailed'));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-md p-5 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {existing ? t('tutoring.editPayment') : t('tutoring.recordPayment')}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {student.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('common.date')}
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('common.amountEur')}
            </label>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={t('tutoring.amountPlaceholder')}
              className="input w-full"
              autoFocus
            />
            {/* Quick-pick presets — common amounts parents prepay. */}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {[50, 100, 150, 200].map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setAmount(String(preset))}
                  className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                    Number(amount) === preset
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  {preset} €
                </button>
              ))}
            </div>
            {suggestedAmount && !existing && (
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {t('tutoring.currentlyOpen', { amount: fmtMoney(suggestedAmount) })}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('todo.noteOptional')}
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('tutoring.paymentNotePlaceholder')}
              className="input w-full"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={handleSave}
              disabled={saving || !valid}
              className="btn bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex-1"
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
            <button onClick={onClose} className="btn-secondary">
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
